import type { ComputedRef, Ref } from 'vue';
import { useI18n } from 'vue-i18n';

type ExportCSVOptions = {
  toBeExported?: Record<string, any>[];
  useRawData?: boolean;
  showAllColumns?: boolean;
  event?: Event;
};

export function useCsvExport(options: {
  dataKey: Ref<string>;
  processedData: ComputedRef<any[]>;
  allColumns: ComputedRef<string[]>;
  visibleColumns: ComputedRef<string[]>;
  sortedData: ComputedRef<any[]>;
}) {
  const { t } = useI18n();

  const buildExportFilename = ({
    toBeExported,
    useRawData = false,
    showAllColumns = false,
  }: ExportCSVOptions): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const translatedKey = t(`navigation.${options.dataKey.value}`, `${options.dataKey.value}`);

    const rawDataSuffix = useRawData ? t(`labels.rawdata`, `rawdata`) : undefined;
    const columnScope = showAllColumns ? undefined : t(`labels.selectedColumns`, `selectedcolumns`);

    const isFullExport = toBeExported?.length === options.processedData.value.length;
    const dataScope = isFullExport ? undefined : t(`labels.filtered`, `filtered`);

    const parts = [timestamp, translatedKey, rawDataSuffix, dataScope, columnScope]
      .filter(Boolean)
      .join('_')
      .replace(/\s+/g, '-');

    return `${parts}.csv`;
  };

  const exportCSV = ({
    toBeExported = [],
    useRawData = false,
    showAllColumns = false,
  }: ExportCSVOptions = {}) => {
    const columns = !!showAllColumns ? options.allColumns.value : options.visibleColumns.value;

    if (!toBeExported.length || !columns.length) {
      console.warn('[exportCSV] No data or columns available for export.');
      return;
    }

    // Build header
    const headers = columns.map((col) => `"${t(`labels.${col}`, col)}"`).join(',');

    const getCellValue = (cell: any, rawData: boolean = false): string => {
      const val = cell?.meta
        ? rawData
          ? cell.meta('rawvalue')
          : cell.meta('fullValue') || cell.meta('renderedValue') || ''
        : cell;

      if (typeof val === 'object' && val !== null) {
        try {
          return JSON.stringify(val);
        } catch {
          return '[unserializable]';
        }
      }

      return String(val ?? '');
    };

    // Build rows
    const rows = toBeExported.map((row) => {
      return columns
        .map((col) => {
          const cell = row[col];
          return `"${String(getCellValue(cell, useRawData)).replace(/"/g, '""')}"`; // CSV escaping
        })
        .join(',');
    });

    const csvContent = [headers, ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = buildExportFilename({ toBeExported, useRawData, showAllColumns });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    link.click();
  };

  const handleExport = (payload: {
    dataType: string;
    exportScope: string;
    columnScope: string;
  }) => {
    exportCSV({
      toBeExported:
        payload.exportScope == 'all' ? options.processedData.value : options.sortedData.value,
      useRawData: payload.dataType === 'raw',
      showAllColumns: payload.columnScope === 'all',
    });
  };

  return {
    buildExportFilename,
    handleExport,
    exportCSV,
  };
}
